import { EquipmentRef } from '@core/referential/equipment-ref.enum'
import { SynopticCategory } from '@core/referential/synoptic-category.enum'
import { SynopticType } from '@core/referential/synoptic-type.enum'
import { z } from 'zod'

import { dateToIsoStringSchema, numberOrStringToNumberSchema } from '@shared/schema/utils'

// type / category sont des enums TS côté core (SynopticType / SynopticCategory), reflétés ici en
// z.nativeEnum pour valider les payloads API sans dupliquer les valeurs.
export const SynopticTypeSchema = z.nativeEnum(SynopticType)

export const SynopticCategorySchema = z.nativeEnum(SynopticCategory)

// Refs du macro-équipement d'un module (fonction du module, refs liables à ses composants) :
// n'importe quelle valeur de l'enum côté validation, le front restreint aux refs parents pertinents.
// Tableau — un module est lié à un ou plusieurs refs (un seul aujourd'hui, le pluriel anticipe).
export const SynopticEquipmentRefsSchema = z.array(z.nativeEnum(EquipmentRef))

// placements / config / variants sont des blobs jsonb persistés tels quels : leur forme précise
// (MachineSchema, CustomComponentLibrary) vit côté web. Le backend les traite comme opaques, on
// valide donc juste qu'ils sont du JSON bien typé sans dupliquer toute la structure du diagramme.
export const SynopticPlacementsSchema = z.array(z.unknown())

export const SynopticConfigSchema = z.record(z.unknown())

export const SynopticVariantsSchema = z.record(z.unknown())

// SynopticSchema est parsé à deux endroits : côté lambda directement sur la row Kysely (id en bigint
// rendu string, timestamps en Date), et côté web sur le JSON HTTP (id déjà number, dates en string
// ISO). Les coercions acceptent donc les deux formes et normalisent vers { id: number, dates: string
// ISO } — toutes deux partagées (cf. schema/utils).
export const SynopticSchema = z.object({
  id: numberOrStringToNumberSchema,
  name: z.string(),
  version: z.number().int(),
  placements: SynopticPlacementsSchema,
  config: SynopticConfigSchema,
  variants: SynopticVariantsSchema,
  type: SynopticTypeSchema,
  category: SynopticCategorySchema,
  // Localisation du synoptic, portée par ses colonnes : `siteId` scope les lectures, `buildingId` /
  // `floorId` le situent dans le site. Null pour un synoptic non rattaché (CTA, composant réutilisable).
  siteId: numberOrStringToNumberSchema.nullable(),
  buildingId: numberOrStringToNumberSchema.nullable(),
  floorId: numberOrStringToNumberSchema.nullable(),
  equipmentRefs: SynopticEquipmentRefsSchema,
  createdAt: dateToIsoStringSchema,
  updatedAt: dateToIsoStringSchema,
  deletedAt: dateToIsoStringSchema.nullable(),
})

export type Synoptic = z.infer<typeof SynopticSchema>
